import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import { updateTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';
import {
  TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING,
  TOURNAMENT_SUBTYPE,
  TOURNAMENT_SUBTYPE_SERVER_TO_CLIENT_MAPPING,
  TOURNAMENT_TYPE,
  TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING
} from 'Src/consts/tournament';
import { AGE_GROUPS } from 'Src/consts/school';
import { DatePicker } from 'Src/components/DatePicker/DatePicker';
import { HTMLEditorQuillHide } from 'Src/components/HTMLEditorQuill/HTMLEditorQuillHide';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { getNameByPlaceOrPostcode, searchFunctionAdmins } from 'Src/helpers/autocomplete/autocomplete';
import {
  convertPlaceToPostcodeWithPlaceName,
  getPostcodes,
  getVenueTypeByPostcode,
  isPlace
} from 'Src/helpers/venue/venue';
import { getSeason } from 'Src/helpers/accessor/accessor';
import { Map } from 'Src/components/Map/Map';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { VenueForm } from 'Src/views/GenericView/AdminView/School/Venues/VenueForm/VenueForm';
import { createVenue } from 'Src/helpers/service/admin/venues';
import { Switch } from 'Src/components/Switch/Switch';

export type SettingsSection = 'details' | 'communications' | 'admins';

interface Props {
  user: AppUser;
  tournament: Tournament;
  activeSection: SettingsSection;
  onSectionChange: (section: SettingsSection) => void;
  onUpdated: (tournament: Tournament) => void;
  onManageAdmins: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

interface DetailsDraft {
  name: string;
  startTime: string;
  endTime: string;
  entriesOpen: string;
  deadlineForEntries: string;
  description: string;
  maxPlayersPerTeam: string;
  isEventParticipantLimitEnforced: boolean;
  maxIndividualEventsPerStudent: string;
  maxRelayEventsPerStudent: string;
  type: string;
  subType: string;
  ages: number[];
  gender: string;
  venue: any;
}

interface CommunicationsDraft {
  useUnionDefaults: boolean;
  enabled: boolean;
  schoolInitiation: 'ORGANISER_ONLY' | 'PARTICIPANTS' | 'PARTICIPANTS_WAITING_LIST' | 'ALL_CONNECTED';
  announcementsEnabled: boolean;
  announcementAudiences: string[];
  autoCloseDaysAfterEnd: number;
}

interface State {
  draft: DetailsDraft;
  baseline: DetailsDraft;
  isSaving: boolean;
  admins: any[];
  isAdminsSaving: boolean;
  isDiscardOpen: boolean;
  isCreateVenueOpen: boolean;
  isEditingDetails: boolean;
  isVenueMapOpen: boolean;
  pendingCloseEdit: boolean;
  pendingSection?: SettingsSection;
  communicationsDraft: CommunicationsDraft;
  communicationsBaseline: CommunicationsDraft;
  isCommunicationsSaving: boolean;
}

const SUBTYPES_BY_TYPE: { [type: string]: string[] } = {
  [TOURNAMENT_TYPE.TEAM_TOURNAMENT]: [
    TOURNAMENT_SUBTYPE.FOOTBALL,
    TOURNAMENT_SUBTYPE.CRICKET,
    TOURNAMENT_SUBTYPE.SAILING,
    TOURNAMENT_SUBTYPE.RUGBY,
    TOURNAMENT_SUBTYPE.NETBALL,
    TOURNAMENT_SUBTYPE.BADMINTON,
    TOURNAMENT_SUBTYPE.WINNERS_ONLY
  ],
  [TOURNAMENT_TYPE.INDIVIDUAL_TOURNAMENT]: [
    TOURNAMENT_SUBTYPE.ATHLETIC_SWIMMING,
    TOURNAMENT_SUBTYPE.REGULAR_INDIVIDUAL,
    TOURNAMENT_SUBTYPE.TRIATHLON,
    TOURNAMENT_SUBTYPE.NATIONAL_TRIATHLON,
    TOURNAMENT_SUBTYPE.CROSS_COUNTRY,
    TOURNAMENT_SUBTYPE.NATIONAL_CROSS_COUNTRY,
    TOURNAMENT_SUBTYPE.PSEUDO_TWO_LEVEL,
    TOURNAMENT_SUBTYPE.PSEUDO_TWO_LEVEL_RUNTIME
  ],
  [TOURNAMENT_TYPE.SINGLE_DOUBLE_TOURNAMENT]: [
    TOURNAMENT_SUBTYPE.BADMINTON,
    TOURNAMENT_SUBTYPE.FENCING,
    TOURNAMENT_SUBTYPE.TENNIS
  ],
  [TOURNAMENT_TYPE.MINI_TEAM_TOURNAMENT]: [TOURNAMENT_SUBTYPE.BADMINTON_MT]
};

const toLocalDateTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const createDraft = (tournament: Tournament): DetailsDraft => ({
  name: tournament.name || '',
  startTime: toLocalDateTime(tournament.startTime),
  endTime: toLocalDateTime(tournament.endTime),
  entriesOpen: toLocalDateTime(tournament.entriesOpen),
  deadlineForEntries: toLocalDateTime(tournament.deadlineForEntries),
  description: tournament.description || '',
  maxPlayersPerTeam: String(tournament.maxPlayersPerTeam || ''),
  isEventParticipantLimitEnforced: tournament.isEventParticipantLimitEnforced !== false,
  maxIndividualEventsPerStudent: String(tournament.maxIndividualEventsPerStudent || ''),
  maxRelayEventsPerStudent: String(tournament.maxRelayEventsPerStudent || ''),
  type: tournament.type || '',
  subType: tournament.subType || '',
  ages: [...(tournament.ages || [])].sort((a, b) => a - b),
  gender: tournament.gender || '',
  venue: tournament.venue || null
});

const createCommunicationsDraft = (tournament: Tournament, union?: any): CommunicationsDraft => {
  const own: any = tournament.communications || {};
  const hasLegacyOverride =
    own.useUnionDefaults === undefined &&
    ['enabled', 'schoolInitiation', 'announcementsEnabled', 'announcementAudiences', 'autoCloseDaysAfterEnd'].some(
      key => own[key] !== undefined
    );
  const useUnionDefaults = own.useUnionDefaults !== false && !hasLegacyOverride;
  const source = useUnionDefaults ? union?.settings?.tournamentComms || {} : own;
  return {
    useUnionDefaults,
    enabled: source.enabled !== false,
    schoolInitiation: source.schoolInitiation || 'ALL_CONNECTED',
    announcementsEnabled: source.announcementsEnabled !== false,
    announcementAudiences: source.announcementAudiences || ['PARTICIPANTS', 'WAITING_LIST', 'INVITED', 'ALL_ENGAGED'],
    autoCloseDaysAfterEnd: Number.isFinite(Number(source.autoCloseDaysAfterEnd))
      ? Number(source.autoCloseDaysAfterEnd)
      : 30
  };
};

export class TournamentSettings extends React.Component<Props, State> {
  state: State = {
    draft: createDraft(this.props.tournament),
    baseline: createDraft(this.props.tournament),
    isSaving: false,
    admins: [...(((this.props.tournament as any).admins || []) as any[])],
    isAdminsSaving: false,
    isDiscardOpen: false,
    isCreateVenueOpen: false,
    isEditingDetails: false,
    isVenueMapOpen: false,
    pendingCloseEdit: false,
    communicationsDraft: createCommunicationsDraft(this.props.tournament, this.props.user.activeSchool),
    communicationsBaseline: createCommunicationsDraft(this.props.tournament, this.props.user.activeSchool),
    isCommunicationsSaving: false
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.tournament !== this.props.tournament && !this.isDirty()) {
      const baseline = createDraft(this.props.tournament);
      const communicationsBaseline = createCommunicationsDraft(this.props.tournament, this.props.user.activeSchool);
      this.setState({
        draft: baseline,
        baseline,
        communicationsDraft: communicationsBaseline,
        communicationsBaseline,
        admins: [...(((this.props.tournament as any).admins || []) as any[])]
      });
      return;
    }
    const wasDirty =
      JSON.stringify(prevState.draft) !== JSON.stringify(prevState.baseline) ||
      JSON.stringify(prevState.communicationsDraft) !== JSON.stringify(prevState.communicationsBaseline);
    const isDirty = this.isDirty();
    if (wasDirty !== isDirty) {
      window.removeEventListener('beforeunload', this.onBeforeUnload);
      if (isDirty) window.addEventListener('beforeunload', this.onBeforeUnload);
      this.props.onDirtyChange(isDirty);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.props.onDirtyChange(false);
  }

  onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.isDirty()) return;
    event.preventDefault();
    event.returnValue = '';
  };

  isDirty = () =>
    JSON.stringify(this.state.draft) !== JSON.stringify(this.state.baseline) ||
    JSON.stringify(this.state.communicationsDraft) !== JSON.stringify(this.state.communicationsBaseline);

  requestSection = (section: SettingsSection) => {
    if (section === this.props.activeSection) return;
    if (this.isDirty()) this.setState({ isDiscardOpen: true, pendingSection: section, pendingCloseEdit: false });
    else this.props.onSectionChange(section);
  };

  saveAdmins = async (admins: any[]) => {
    if (this.state.isAdminsSaving) return;
    this.setState({ isAdminsSaving: true });
    try {
      const tournament = await updateTournament(this.props.user, { admins }, this.props.tournament.id);
      const savedAdmins = [...(((tournament as any).admins || admins) as any[])];
      this.setState({ admins: savedAdmins, isAdminsSaving: false });
      this.props.onUpdated(tournament);
    } catch (error) {
      this.setState({ isAdminsSaving: false });
    }
  };

  addAdmin = (admin: any) => {
    const userId = admin?.id || admin?._id;
    if (!userId || this.state.admins.some(item => String(item.userId || item.id) === String(userId))) return;
    const next = [
      ...this.state.admins,
      {
        userId,
        permissionId: admin.permissionId,
        firstName: admin.firstName,
        lastName: admin.lastName,
        email: admin.email,
        name: [admin.firstName, admin.lastName].filter(Boolean).join(' ') || admin.name
      }
    ];
    this.setState({ admins: next }, () => this.saveAdmins(next));
  };

  removeAdmin = (admin: any) => {
    const id = String(admin.userId || admin.id || admin._id);
    const next = this.state.admins.filter(item => String(item.userId || item.id || item._id) !== id);
    this.setState({ admins: next }, () => this.saveAdmins(next));
  };

  saveCommunications = async () => {
    if (this.state.isCommunicationsSaving) return;
    this.setState({ isCommunicationsSaving: true });
    try {
      const tournament = await updateTournament(
        this.props.user,
        { communications: this.state.communicationsDraft },
        this.props.tournament.id
      );
      const baseline = createCommunicationsDraft(tournament, this.props.user.activeSchool);
      this.setState(
        { communicationsDraft: baseline, communicationsBaseline: baseline, isCommunicationsSaving: false },
        () => {
          this.props.onDirtyChange(false);
          this.props.onUpdated(tournament);
        }
      );
    } catch (error) {
      this.setState({ isCommunicationsSaving: false });
    }
  };

  renderCommunications = () => {
    const draft = this.state.communicationsDraft;
    const platformAvailable = (this.props.user.activeSchool as any)?.tournamentCommunicationsAvailable !== false;
    const audienceOptions = [
      ['PARTICIPANTS', 'Confirmed participants'],
      ['WAITING_LIST', 'Waiting list'],
      ['INVITED', 'Invited schools'],
      ['ALL_ENGAGED', 'All connected schools']
    ];
    const update = (values: Partial<CommunicationsDraft>) =>
      this.setState(state => ({ communicationsDraft: { ...state.communicationsDraft, ...values } }));
    const dirty = JSON.stringify(draft) !== JSON.stringify(this.state.communicationsBaseline);
    return (
      <article className="utmSettings__card utmSettingsComms">
        <header>
          <div>
            <h2>Tournament chats</h2>
            <p>Control private school conversations, organiser announcements and what happens after the tournament.</p>
          </div>
          <button
            type="button"
            className="is-primary"
            disabled={!platformAvailable || !dirty || this.state.isCommunicationsSaving}
            onClick={this.saveCommunications}
          >
            {this.state.isCommunicationsSaving ? 'Saving…' : 'Save settings'}
          </button>
        </header>
        {!platformAvailable ? (
          <div className="utmSettingsComms__notAvailable">
            <strong>Tournament chats are not included for this union</strong>
            <span>
              Contact Squad In Touch support to enable secure tournament communications. Existing safeguarding history
              remains available as read-only.
            </span>
            <a href="mailto:support@squadintouch.com?subject=Enable%20Tournament%20Communications">Contact support</a>
          </div>
        ) : null}
        <section className={!platformAvailable ? 'is-disabled' : ''}>
          <div>
            <strong>Use union defaults</strong>
            <small>
              Keep this tournament aligned with Union settings. Turn this off only when this tournament needs different
              chat rules.
            </small>
          </div>
          <Switch
            name="tournament-use-union-defaults"
            disabled={!platformAvailable}
            checked={draft.useUnionDefaults}
            onChange={event =>
              event.target.checked
                ? this.setState({
                    communicationsDraft: createCommunicationsDraft(
                      { ...this.props.tournament, communications: { useUnionDefaults: true } as any },
                      this.props.user.activeSchool
                    )
                  })
                : update({ useUnionDefaults: false })
            }
          />
        </section>
        <section className={!platformAvailable || draft.useUnionDefaults ? 'is-disabled' : ''}>
          <div>
            <strong>Enable tournament chats</strong>
            <small>
              When disabled, previous conversations remain available as read-only safeguarding history, but nobody can
              start chats or send messages.
            </small>
          </div>
          <Switch
            name="tournament-chats-enabled"
            disabled={!platformAvailable || draft.useUnionDefaults}
            checked={platformAvailable && draft.enabled}
            onChange={event => update({ enabled: event.target.checked })}
          />
        </section>
        <section className={!draft.enabled || draft.useUnionDefaults ? 'is-disabled' : ''}>
          <label>
            <span>Who can start a private chat?</span>
            <small>The organiser can always start a chat while tournament chats are enabled.</small>
            <select
              disabled={!draft.enabled || draft.useUnionDefaults}
              value={draft.schoolInitiation}
              onChange={event =>
                update({ schoolInitiation: event.target.value as CommunicationsDraft['schoolInitiation'] })
              }
            >
              <option value="ORGANISER_ONLY">Organiser only</option>
              <option value="PARTICIPANTS">Confirmed participants</option>
              <option value="PARTICIPANTS_WAITING_LIST">Participants and waiting list</option>
              <option value="ALL_CONNECTED">Participants, waiting list and invited schools</option>
            </select>
          </label>
        </section>
        <section className={!draft.enabled || draft.useUnionDefaults ? 'is-disabled' : ''}>
          <div>
            <strong>Organiser announcements</strong>
            <small>Publish a read-only update to one of the enabled tournament audiences.</small>
          </div>
          <Switch
            name="tournament-announcements-enabled"
            disabled={!draft.enabled || draft.useUnionDefaults}
            checked={draft.announcementsEnabled}
            onChange={event => update({ announcementsEnabled: event.target.checked })}
          />
          {draft.announcementsEnabled && draft.enabled ? (
            <div className="utmSettingsComms__audiences">
              {audienceOptions.map(([value, label]) => (
                <label key={value}>
                  <input
                    type="checkbox"
                    disabled={draft.useUnionDefaults}
                    checked={draft.announcementAudiences.includes(value)}
                    onChange={event =>
                      update({
                        announcementAudiences: event.target.checked
                          ? [...draft.announcementAudiences, value]
                          : draft.announcementAudiences.filter(item => item !== value)
                      })
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          ) : null}
        </section>
        <section className={!draft.enabled || draft.useUnionDefaults ? 'is-disabled' : ''}>
          <label>
            <span>Close conversations after the tournament</span>
            <small>
              Open chats become read-only safeguarding history after this many days. Select Never to manage them
              manually.
            </small>
            <select
              disabled={!draft.enabled || draft.useUnionDefaults}
              value={draft.autoCloseDaysAfterEnd}
              onChange={event => update({ autoCloseDaysAfterEnd: Number(event.target.value) })}
            >
              <option value={0}>Never</option>
              <option value={1}>After 1 day</option>
              <option value={7}>After 7 days</option>
              <option value={14}>After 14 days</option>
              <option value={30}>After 30 days</option>
              <option value={90}>After 90 days</option>
            </select>
          </label>
        </section>
      </article>
    );
  };

  updateDraft = (field: keyof DetailsDraft, value: any) =>
    this.setState(state => ({ draft: { ...state.draft, [field]: value } }));

  saveDetails = async () => {
    if (this.state.isSaving || !this.state.draft.name.trim()) return;
    const draft = this.state.draft;
    this.setState({ isSaving: true });
    try {
      const tournament = await updateTournament(
        this.props.user,
        {
          name: draft.name.trim(),
          startTime: new Date(draft.startTime).toISOString(),
          endTime: new Date(draft.endTime).toISOString(),
          entriesOpen: draft.entriesOpen ? new Date(draft.entriesOpen).toISOString() : null,
          deadlineForEntries: draft.deadlineForEntries ? new Date(draft.deadlineForEntries).toISOString() : null,
          description: draft.description,
          maxPlayersPerTeam: Number(draft.maxPlayersPerTeam || 0),
          isEventParticipantLimitEnforced: draft.isEventParticipantLimitEnforced,
          maxIndividualEventsPerStudent: Number(draft.maxIndividualEventsPerStudent || 0),
          maxRelayEventsPerStudent: Number(draft.maxRelayEventsPerStudent || 0),
          type: draft.type,
          subType: draft.subType,
          ages: draft.ages,
          gender: draft.gender,
          venue: draft.venue
        },
        this.props.tournament.id
      );
      const baseline = createDraft(tournament);
      this.setState({ draft: baseline, baseline, isSaving: false, isEditingDetails: false }, () => {
        this.props.onDirtyChange(false);
        this.props.onUpdated(tournament);
      });
    } catch (error) {
      this.setState({ isSaving: false });
    }
  };

  createAndSelectVenue = async (data: any) => {
    const venue = await createVenue(this.props.user, data);
    const selectedVenue: any = convertPlaceToPostcodeWithPlaceName(venue);
    selectedVenue.postcodeId = selectedVenue.postcodeId || selectedVenue.id;
    selectedVenue.venueType = getVenueTypeByPostcode(selectedVenue, this.props.user.activeSchool);
    this.setState(state => ({
      draft: { ...state.draft, venue: selectedVenue },
      isCreateVenueOpen: false
    }));
  };

  renderDetails = () => {
    const tournament = this.props.tournament;
    const draft = this.state.draft;
    const genderOptions = [
      tournament.sport?.genders?.maleOnly && 'MALE_ONLY',
      tournament.sport?.genders?.femaleOnly && 'FEMALE_ONLY',
      tournament.sport?.genders?.mixed && 'MIXED'
    ].filter(Boolean) as string[];
    if (draft.gender && !genderOptions.includes(draft.gender)) genderOptions.push(draft.gender);
    const teamsWillChange =
      JSON.stringify(draft.ages) !== JSON.stringify([...(tournament.ages || [])].sort((a, b) => a - b)) ||
      draft.gender !== tournament.gender;
    const isTeamTournament = draft.type !== 'INDIVIDUAL_TOURNAMENT';
    const subtypeOptions = SUBTYPES_BY_TYPE[draft.type] || [];
    const venueName = draft.venue?.name || draft.venue?.placeName || draft.venue?.venueName || 'Venue not set';
    const venuePostcode = draft.venue?.postcode || draft.venue?.postcodeName || '';
    const formatDate = (value: string) =>
      value
        ? new Date(value).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : 'Not set';
    return (
      <article className="utmSettings__card utmSettingsDetails">
        <header>
          <div>
            <h2>Details</h2>
            <p>Name, dates, venue and competition type. Changing dates notifies participant schools.</p>
          </div>
          <div>
            {this.state.isEditingDetails ? (
              <>
                {this.isDirty() ? <span className="utmSettingsDetails__unsaved">Unsaved changes</span> : null}
                <button
                  type="button"
                  className="is-secondary"
                  onClick={() =>
                    this.isDirty()
                      ? this.setState({ isDiscardOpen: true, pendingCloseEdit: true })
                      : this.setState({ isEditingDetails: false, isVenueMapOpen: false })
                  }
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="is-primary"
                  disabled={
                    !this.isDirty() ||
                    this.state.isSaving ||
                    !draft.name.trim() ||
                    !draft.type ||
                    !draft.subType ||
                    !draft.gender ||
                    !draft.ages.length ||
                    !draft.startTime ||
                    !draft.endTime
                  }
                  onClick={this.saveDetails}
                >
                  {this.state.isSaving ? 'Saving…' : 'Save changes'}
                </button>
              </>
            ) : (
              <button type="button" className="is-primary" onClick={() => this.setState({ isEditingDetails: true })}>
                Quick edit
              </button>
            )}
          </div>
        </header>
        <div className="utmSettingsDetails__contextChips">
          <span className="is-sport">{tournament.sport?.name || 'Sport not set'}</span>
          <span className="is-season">{getSeason(tournament)}</span>
        </div>
        {!this.state.isEditingDetails ? (
          <div className="utmSettingsDetails__readGrid">
            <div className="is-wide">
              <span>Tournament name</span>
              <strong>{draft.name || '—'}</strong>
            </div>
            <div>
              <span>Type</span>
              <strong>{TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING[draft.type] || draft.type || '—'}</strong>
            </div>
            <div>
              <span>Subtype</span>
              <strong>{TOURNAMENT_SUBTYPE_SERVER_TO_CLIENT_MAPPING[draft.subType] || draft.subType || '—'}</strong>
            </div>
            <div>
              <span>Starts</span>
              <strong>{formatDate(draft.startTime)}</strong>
            </div>
            <div>
              <span>Ends</span>
              <strong>{formatDate(draft.endTime)}</strong>
            </div>
            <div>
              <span>Booking opens</span>
              <strong>{formatDate(draft.entriesOpen)}</strong>
            </div>
            <div>
              <span>Booking closes</span>
              <strong>{formatDate(draft.deadlineForEntries)}</strong>
            </div>
            <div>
              <span>Gender</span>
              <strong>{TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[draft.gender] || draft.gender || '—'}</strong>
            </div>
            <div className="is-wide utmSettingsDetails__limitsSummary">
              <span>Participation limits</span>
              <div>
                <div>
                  <small>Max players per team</small>
                  <strong>{draft.maxPlayersPerTeam || 'Unlimited'}</strong>
                </div>
                {draft.type === TOURNAMENT_TYPE.INDIVIDUAL_TOURNAMENT ? (
                  <>
                    <div>
                      <small>Participant limit in events</small>
                      <strong>{draft.isEventParticipantLimitEnforced ? 'Enforced' : 'Not enforced'}</strong>
                    </div>
                    <div>
                      <small>Max individual events per student</small>
                      <strong>{draft.maxIndividualEventsPerStudent || 'Unlimited'}</strong>
                    </div>
                    <div>
                      <small>Max relay events per student</small>
                      <strong>{draft.maxRelayEventsPerStudent || 'Unlimited'}</strong>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
            <div className="is-wide">
              <span>Ages</span>
              <div className="utmSettingsDetails__readChips">
                {draft.ages.map(age => (
                  <b key={age}>{AGE_GROUPS.ENGLISH[age]}</b>
                ))}
              </div>
            </div>
            <div className="is-wide utmSettingsDetails__readVenue">
              <div>
                <span>Venue</span>
                <strong>
                  {venueName}
                  {venuePostcode ? ` · ${venuePostcode}` : ''}
                </strong>
              </div>
              {draft.venue?.point?.coordinates?.length === 2 ? (
                <button
                  type="button"
                  onClick={() => this.setState(state => ({ isVenueMapOpen: !state.isVenueMapOpen }))}
                >
                  {this.state.isVenueMapOpen ? 'Hide map' : 'Show map'}
                </button>
              ) : null}
              {this.state.isVenueMapOpen && draft.venue?.point?.coordinates?.length === 2 ? (
                <Map
                  key={draft.venue.point.coordinates.join(',')}
                  point={draft.venue.point}
                  customStylingClass="utmSettingsDetails__venueMap"
                  isMarkerDraggable={false}
                />
              ) : null}
            </div>
            <div className="is-wide utmSettingsDetails__readDescription">
              <span>Description</span>
              {draft.description ? (
                <div dangerouslySetInnerHTML={{ __html: draft.description }} />
              ) : (
                <strong>Not provided</strong>
              )}
            </div>
          </div>
        ) : (
          <div className="utmSettingsDetails__grid">
            <div className="utmSettingsDetails__sectionTitle">
              <b>1</b>
              <div>
                <strong>Core details</strong>
                <small>The tournament identity and competition format.</small>
              </div>
            </div>
            <label className="is-wide is-required">
              <span>Tournament name</span>
              <input value={draft.name} onChange={event => this.updateDraft('name', event.target.value)} />
            </label>
            <label className="is-required">
              <span>Competition type</span>
              <select
                value={draft.type}
                onChange={event => {
                  const type = event.target.value;
                  const nextSubtypes = SUBTYPES_BY_TYPE[type] || [];
                  this.setState(state => ({
                    draft: {
                      ...state.draft,
                      type,
                      subType: nextSubtypes.includes(state.draft.subType) ? state.draft.subType : nextSubtypes[0] || '',
                      ages:
                        type !== 'INDIVIDUAL_TOURNAMENT' && state.draft.ages.length > 1
                          ? [state.draft.ages[0]]
                          : state.draft.ages
                    }
                  }));
                }}
              >
                {Object.keys(TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING).map(value => (
                  <option key={value} value={value}>
                    {TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING[value]}
                  </option>
                ))}
              </select>
            </label>
            <label className="is-required">
              <span>Competition subtype</span>
              <select value={draft.subType} onChange={event => this.updateDraft('subType', event.target.value)}>
                <option value="">Not set</option>
                {subtypeOptions.map(value => (
                  <option key={value} value={value}>
                    {TOURNAMENT_SUBTYPE_SERVER_TO_CLIENT_MAPPING[value]}
                  </option>
                ))}
              </select>
            </label>
            <div className="utmSettingsDetails__sectionTitle">
              <b>2</b>
              <div>
                <strong>Schedule</strong>
                <small>When the tournament starts and finishes.</small>
              </div>
            </div>
            <label className="is-required">
              <span>Starts</span>
              <DatePicker
                mode="datetime"
                value={draft.startTime ? new Date(draft.startTime) : null}
                onChange={date =>
                  this.updateDraft('startTime', date instanceof Date ? toLocalDateTime(date.toISOString()) : '')
                }
                placeholder="Select start date and time"
                minuteStep={15}
                placement="bottom"
                clearable={false}
              />
            </label>
            <label className="is-required">
              <span>Ends</span>
              <DatePicker
                mode="datetime"
                value={draft.endTime ? new Date(draft.endTime) : null}
                onChange={date =>
                  this.updateDraft('endTime', date instanceof Date ? toLocalDateTime(date.toISOString()) : '')
                }
                min={draft.startTime ? new Date(draft.startTime) : undefined}
                placeholder="Select end date and time"
                minuteStep={15}
                placement="bottom"
                clearable={false}
              />
            </label>
            <div className="utmSettingsDetails__sectionTitle">
              <b>3</b>
              <div>
                <strong>Booking window</strong>
                <small>When schools can submit and update their entries.</small>
              </div>
            </div>
            <label>
              <span>Booking opens</span>
              <DatePicker
                mode="datetime"
                value={draft.entriesOpen ? new Date(draft.entriesOpen) : null}
                onChange={date =>
                  this.updateDraft('entriesOpen', date instanceof Date ? toLocalDateTime(date.toISOString()) : '')
                }
                clearable
                placeholder="Select opening date and time"
                minuteStep={15}
                placement="bottom"
              />
            </label>
            <label>
              <span>Booking closes</span>
              <DatePicker
                mode="datetime"
                value={draft.deadlineForEntries ? new Date(draft.deadlineForEntries) : null}
                onChange={date =>
                  this.updateDraft(
                    'deadlineForEntries',
                    date instanceof Date ? toLocalDateTime(date.toISOString()) : ''
                  )
                }
                clearable
                min={draft.entriesOpen ? new Date(draft.entriesOpen) : undefined}
                placeholder="Select closing date and time"
                minuteStep={15}
                placement="bottom"
              />
            </label>
            <div className="utmSettingsDetails__sectionTitle">
              <b>4</b>
              <div>
                <strong>Additional information</strong>
                <small>Audience, venue and participant settings.</small>
              </div>
            </div>
            <div className="utmSettingsDetails__venueBlock">
              <div className="utmSettingsDetails__venueHeader">
                <div>
                  <strong>Venue</strong>
                  <small>Search for an existing venue or create a new one.</small>
                </div>
                <button type="button" onClick={() => this.setState({ isCreateVenueOpen: true })}>
                  + Add new venue
                </button>
              </div>
              <Autocomplete
                placeholder="Start typing the postcode or venue name"
                searchFunction={text => getPostcodes(text, this.props.user)}
                getElementTitle={getNameByPlaceOrPostcode}
                customClass="mFullWidth"
                defaultItem={draft.venue}
                onSelect={item => {
                  const venue: any = isPlace(item) ? convertPlaceToPostcodeWithPlaceName(item) : item;
                  if (venue && venue.point) {
                    if (!venue.postcodeId && venue.id) venue.postcodeId = venue.id;
                    venue.venueType = getVenueTypeByPostcode(venue, this.props.user.activeSchool);
                  }
                  this.updateDraft('venue', venue);
                }}
              />
              {draft.venue?.point?.coordinates?.length === 2 ? (
                <button
                  type="button"
                  className="utmSettingsDetails__mapToggle"
                  onClick={() => this.setState(state => ({ isVenueMapOpen: !state.isVenueMapOpen }))}
                >
                  {this.state.isVenueMapOpen ? 'Hide map' : 'Show map'}
                </button>
              ) : null}
              {this.state.isVenueMapOpen && draft.venue?.point?.coordinates?.length === 2 ? (
                <Map
                  key={draft.venue.point.coordinates.join(',')}
                  point={draft.venue.point}
                  customStylingClass="utmSettingsDetails__venueMap"
                  isMarkerDraggable={false}
                />
              ) : null}
            </div>
            <label className="is-required">
              <span>Gender</span>
              <div className="utmSettingsDetails__genderChips">
                {genderOptions.map(value => (
                  <button
                    type="button"
                    key={value}
                    className={draft.gender === value ? 'is-selected' : ''}
                    onClick={() => this.updateDraft('gender', value)}
                  >
                    {TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[value] || value}
                  </button>
                ))}
              </div>
            </label>
            <div className="utmSettingsDetails__limitsBlock">
              <div className="utmSettingsDetails__limitsHeader">
                <strong>Participation limits</strong>
                <small>Use 0 or leave a number blank when no maximum should apply.</small>
              </div>
              <div className="utmSettingsDetails__limitsGrid">
                <label className="utmSettingsDetails__maxPlayers">
                  <span>Max players per team</span>
                  <input
                    type="number"
                    min="0"
                    value={draft.maxPlayersPerTeam}
                    onChange={event => this.updateDraft('maxPlayersPerTeam', event.target.value)}
                  />
                </label>
                {draft.type === TOURNAMENT_TYPE.INDIVIDUAL_TOURNAMENT ? (
                  <>
                    <label className="utmSettingsDetails__maxPlayers">
                      <span>Max individual events per student</span>
                      <input
                        type="number"
                        min="0"
                        value={draft.maxIndividualEventsPerStudent}
                        onChange={event => this.updateDraft('maxIndividualEventsPerStudent', event.target.value)}
                      />
                    </label>
                    <label className="utmSettingsDetails__maxPlayers">
                      <span>Max relay events per student</span>
                      <input
                        type="number"
                        min="0"
                        value={draft.maxRelayEventsPerStudent}
                        onChange={event => this.updateDraft('maxRelayEventsPerStudent', event.target.value)}
                      />
                    </label>
                  </>
                ) : null}
              </div>
              {draft.type === TOURNAMENT_TYPE.INDIVIDUAL_TOURNAMENT ? (
                <div className="utmSettingsDetails__limitSwitch">
                  <div>
                    <strong>Enforce participant limit in events</strong>
                    <small>
                      Apply each sport’s maximum participants per team when students are allocated to events.
                    </small>
                  </div>
                  <Switch
                    name="isEventParticipantLimitEnforced"
                    checked={draft.isEventParticipantLimitEnforced}
                    onChange={event => this.updateDraft('isEventParticipantLimitEnforced', event.target.checked)}
                  />
                </div>
              ) : null}
            </div>
            <label className="is-wide is-required">
              <span>Ages</span>
              <div className="utmSettingsDetails__ageChips">
                {AGE_GROUPS.ENGLISH.map((label, age) => (
                  <button
                    type="button"
                    key={age}
                    className={draft.ages.includes(age) ? 'is-selected' : ''}
                    onClick={() =>
                      this.updateDraft(
                        'ages',
                        isTeamTournament
                          ? [age]
                          : draft.ages.includes(age)
                          ? draft.ages.filter(value => value !== age)
                          : [...draft.ages, age].sort((a, b) => a - b)
                      )
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              <small className="utmSettingsDetails__fieldHint">
                {isTeamTournament
                  ? 'Team tournaments use one age group.'
                  : 'Select every age group included in this tournament.'}
              </small>
              {teamsWillChange ? (
                <small className="utmSettingsDetails__teamWarning">
                  Existing tournament teams will be updated to match the selected ages and gender.
                </small>
              ) : null}
            </label>
            <label className="is-wide utmSettingsDetails__editor">
              <span>Description</span>
              <small className="utmSettingsDetails__fieldHint">
                This information appears prominently at the top of the tournament page. Use it to highlight important
                details, instructions or other information participants should see first.
              </small>
              <HTMLEditorQuillHide
                user={this.props.user}
                value={draft.description}
                onTextChange={value => this.updateDraft('description', value)}
                isEnabled
              />
            </label>
          </div>
        )}
      </article>
    );
  };

  renderAdmins = () => (
    <article className="utmSettings__card utmSettingsAdmins">
      <header>
        <div>
          <h2>
            Admins <b>{this.state.admins.length}</b>
          </h2>
          <p>Union administrators assigned to manage this tournament and enter results.</p>
        </div>
        {this.state.isAdminsSaving ? <span className="utmSettingsAdmins__saving">Saving…</span> : null}
      </header>
      <div className="utmSettingsAdmins__adder">
        <strong>Add tournament admin</strong>
        <small>Search administrators of this union by name or email.</small>
        <Autocomplete
          key={this.state.admins.map(admin => admin.userId || admin.id).join('|')}
          placeholder="Start typing admin name or email"
          searchFunction={text =>
            searchFunctionAdmins(this.props.user, text, this.props.user.activeSchoolId, this.state.admins)
          }
          getElementTitle={admin => {
            const name = [admin.firstName, admin.lastName].filter(Boolean).join(' ') || admin.name || '';
            return admin.email ? `${name} — ${admin.email}` : name;
          }}
          customClass="mFullWidth"
          onSelect={this.addAdmin}
        />
      </div>
      <div className="utmSettingsAdmins__tableWrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {this.state.admins.map(admin => (
              <tr key={admin.userId || admin.id || admin._id}>
                <td>
                  <strong>
                    {[admin.firstName, admin.lastName].filter(Boolean).join(' ') || admin.name || 'Unnamed admin'}
                  </strong>
                </td>
                <td>{admin.email || '—'}</td>
                <td>
                  <span className="utmSettingsAdmins__role">{admin.role || 'Tournament admin'}</span>
                </td>
                <td className="utmSettingsAdmins__actions">
                  <button type="button" disabled={this.state.isAdminsSaving} onClick={() => this.removeAdmin(admin)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {!this.state.admins.length ? (
              <tr>
                <td colSpan={4} className="utmSettingsAdmins__empty">
                  No tournament admins found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </article>
  );

  render() {
    return (
      <main className="utmSettings">
        <nav className="utmSettings__subtabs">
          <button
            type="button"
            className={this.props.activeSection === 'details' ? 'is-active' : ''}
            onClick={() => this.requestSection('details')}
          >
            Details
          </button>
          <button
            type="button"
            className={this.props.activeSection === 'communications' ? 'is-active' : ''}
            onClick={() => this.requestSection('communications')}
          >
            Communications
          </button>
          <button
            type="button"
            className={this.props.activeSection === 'admins' ? 'is-active' : ''}
            onClick={() => this.requestSection('admins')}
          >
            Admins <b>{this.state.admins.length}</b>
          </button>
        </nav>
        <div className="utmSettings__content">
          {this.props.activeSection === 'admins'
            ? this.renderAdmins()
            : this.props.activeSection === 'communications'
            ? this.renderCommunications()
            : this.renderDetails()}
        </div>
        <UnsavedChangesModal
          isOpen={this.state.isDiscardOpen}
          message="Your tournament settings have not been saved. If you leave now, they will be lost."
          onKeepEditing={() =>
            this.setState({ isDiscardOpen: false, pendingSection: undefined, pendingCloseEdit: false })
          }
          onDiscard={() => {
            const pending = this.state.pendingSection;
            const closeEdit = this.state.pendingCloseEdit;
            this.setState(
              {
                draft: this.state.baseline,
                communicationsDraft: this.state.communicationsBaseline,
                isDiscardOpen: false,
                pendingSection: undefined,
                pendingCloseEdit: false,
                isEditingDetails: closeEdit ? false : this.state.isEditingDetails,
                isVenueMapOpen: false
              },
              () => pending && this.props.onSectionChange(pending)
            );
          }}
        />
        <SimpleModal
          isOpen={this.state.isCreateVenueOpen}
          title="Add new venue"
          onCloseClick={() => this.setState({ isCreateVenueOpen: false })}
        >
          <VenueForm
            user={this.props.user}
            onCancel={() => this.setState({ isCreateVenueOpen: false })}
            onSubmit={this.createAndSelectVenue}
          />
        </SimpleModal>
      </main>
    );
  }
}
